<template>
  <h3>Billing</h3>
  <div v-if="organizations.length >= 1" class="form-group">
    <div class="form-row">
      <OrganizationSelect v-bind="$attrs" :organizations="organizations" :organization-id="organizationId" />
    </div>
    <div class="form-row">
      <div class="form-col">
        <select v-model="formCurrency" required>
          <option :value="null" disabled>Default Currency</option>
          <option v-for="c in formCurrencies" :key="c.id" :value="c.code">
            {{ c.code }}
          </option>
        </select>
      </div>
      <div class="form-col">
        <button @click="saveCurrency">Save Default Currency</button>
      </div>
    </div>
    <div :class="{ loading: formIsLoading }" class="form-row">
      <Result v-bind="$attrs" v-model:error="formError" v-model:is-success="formIsSuccess" />
      <div class="form-row">
        <div class="radiolabel" :class="{ on: formIsOrganization }" @click="formIsOrganization = true">
          <div class="radio" />
          <label>Organization Cards</label>
        </div>
        <div class="radiolabel" :class="{ on: !formIsOrganization }" @click="formIsOrganization = false">
          <div class="radio" />
          <label>Individual Cards</label>
        </div>
      </div>
    </div>
  </div>
  <div class="form-group">
    <h4>Add Credit Card</h4>
    <CardForm v-model:is-new="isNewCard" :organization-id="formIsOrganization ? +organizationId : null" />
  </div>
  <div v-if="tableItems.length > 0" class="form-group">
    <h4>Credit Card List</h4>
    <p class="infomsg">We charge the default payment method for bookings and fees unless otherwise advised. If you have payment methods with different currencies for offices in different countries, please get in touch to enable sub accounts. You may also set a default currency above, which is used for all team members.</p>
    <div>
      <table :class="{ loading: tableIsLoading }">
        <thead>
          <tr>
            <th>Type</th>
            <th>Label</th>
            <th>Last 4 Digits</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="tableItems.length == 0">
            <td colspan="4">No results</td>
          </tr>
          <tr v-for="item in tableItems" v-else :key="item.id">
            <td>Credit Card</td>
            <td v-if="item.id != editId">{{ item.label }}</td>
            <td v-else class="edit">
              <div class="edit-wrapper">
                <input v-model="editLabel" maxlength="50" type="text" />
                <button @click="actionEditSave(item)">Save</button>
                <button class="cancel" @click="actionEditCancel">Cancel</button>
              </div>
            </td>
            <td>{{ item.number }}</td>
            <td class="actions">
              <a v-if="!item.is_default" @click="actionDefault(item)">Set as Default</a>
              <span v-if="item.is_default">Default</span>
              <a @click="actionDelete(item)">Delete</a>
              <a @click="actionEdit(item)">Edit</a>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, inject } from "vue";
import CardForm from "../components/common/CardForm.vue";
import OrganizationSelect from "../components/common/OrganizationSelect.vue";
import Result from "../components/common/FormResult.vue";
import useApi from "../utils/apiCalls.js";

const props = defineProps(["organizations", "organizationId"]);
const emit = defineEmits(["error", "info", "scroll", "refreshOrganizations"]);

const userId = inject("userId");

// Reactive state
const formName = ref(null);
const formCurrency = ref(null);
const formCurrencies = ref([]);
const formIsOrganization = ref(props.organizations.length !== 0);
const formIsLoading = ref(true);
const formIsSuccess = ref(false);
const formError = ref(null);
const isNewCard = ref(false);
const tableItems = ref([]);
const tableIsLoading = ref(true);
const editId = ref(null);
const editLabel = ref(null);
// Payment type is always credit card now

// API Utilities
const { apiGetRequest, apiPutRequest, apiDeleteRequest } = useApi(emit);

// Watchers
watch(isNewCard, () => {
  if (isNewCard.value) {
    loadPaymentMethods();
    isNewCard.value = false;
  }
});

watch(
  () => props.organizations,
  () => {
    loadPaymentMethods();
    loadCurrency();
  }
);

watch(
  () => props.organizationId,
  () => {
    loadPaymentMethods();
    loadCurrency();
  }
);

watch(formIsOrganization, () => {
  loadPaymentMethods();
  loadCurrency();
});

// Lifecycle Hooks
onMounted(() => {
  loadPaymentMethods();
  loadCurrency();
});

// Methods
const actionDefault = item => {
  tableIsLoading.value = true;
  const basePath = formIsOrganization.value ? `/organizations/${props.organizationId}` : `/customers/${userId.value}`;
  const path = `${basePath}/cards/${item.id}`;

  apiPutRequest({
    path,
    onSuccess: () => {
      loadPaymentMethods();
      tableIsLoading.value = false;
    },
    onError: error => {
      tableIsLoading.value = false;
      emit("error", error);
    },
  });
};

const actionDelete = item => {
  const basePath = formIsOrganization.value ? `/organizations/${props.organizationId}` : `/customers/${userId.value}`;
  const path = `${basePath}/cards/${item.id}`;

  if (confirm("Are you sure you want to delete this credit card?")) {
    tableIsLoading.value = true;
    apiDeleteRequest({
      path,
      onSuccess: () => {
        loadPaymentMethods();
        tableIsLoading.value = false;
      },
      onError: error => {
        tableIsLoading.value = false;
        emit("error", error);
      },
    });
  }
};

const actionEdit = item => {
  editId.value = item.id;
  editLabel.value = item.label;
};

const actionEditCancel = () => {
  editId.value = null;
  editLabel.value = null;
};

const actionEditSave = () => {
  tableIsLoading.value = true;
  const basePath = formIsOrganization.value ? `/organizations/${props.organizationId}` : `/customers/${userId.value}`;
  const path = `${basePath}/cards/${editId.value}/label`;

  apiPutRequest({
    path,
    data: {
      label: editLabel.value,
    },
    onSuccess: () => {
      loadPaymentMethods();
      tableIsLoading.value = false;
      editId.value = null;
    },
    onError: error => {
      tableIsLoading.value = false;
      emit("error", error);
      editId.value = null;
    },
  });
};

const loadPaymentMethods = async () => {
  tableIsLoading.value = true;
  const basePath = formIsOrganization.value ? `/organizations/${props.organizationId}` : `/customers/${userId.value}`;

  apiGetRequest({
    path: `${basePath}/cards`,
    onSuccess: response => {
      tableItems.value = response;
      tableIsLoading.value = false;

      // Only credit cards are supported now
    },
    onError: error => {
      tableIsLoading.value = false;
      emit("error", error);
    },
  });
};

const loadOrganization = () => {
  const org = props.organizations.find(org => org.id == props.organizationId);
  if (!org) {
    emit("error", "Organization not found");
    return;
  }

  formName.value = org.name;
  formCurrency.value = org.currency;
};

const loadCurrency = () => {
  if (props.organizationId == null || !formIsOrganization.value) {
    return;
  }

  if (formCurrencies.value.length === 0) {
    formIsLoading.value = true;

    apiGetRequest({
      path: "/currencies",
      onSuccess: response => {
        formCurrencies.value = response;
        formIsLoading.value = false;
      },
      onError: error => {
        formError.value = error;
        formIsLoading.value = false;
        emit("error", error);
      },
    });
  }

  loadOrganization();
};

const saveCurrency = () => {
  if (props.organizationId == null || !formIsOrganization.value) {
    return;
  }

  formIsLoading.value = true;

  apiPutRequest({
    path: `/organizations/${props.organizationId}`,
    data: {
      name: formName.value,
      currency: formCurrency.value,
    },
    onSuccess: () => {
      formIsSuccess.value = true;
      formError.value = null;
      formIsLoading.value = false;
      emit("refreshOrganizations");
    },
    onError: error => {
      formIsSuccess.value = false;
      formError.value = error;
      formIsLoading.value = false;
      emit("error", error);
    },
  });
};
</script>

<style lang="less">
.edit-wrapper {
  display: flex;
  align-items: center;

  input {
    flex: 1;
  }
  button {
    margin-left: 1rem;
    flex-shrink: 0;
  }

  button.cancel {
    background: @secondary;
    border-color: @secondary;
  }
}
</style>
